import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detects that this tab is running an outdated bundle.
 *
 * Hashed assets are served `immutable`, so a long-lived tab holding a stale
 * index.html keeps resolving the old chunk URLs — indefinitely, and silently.
 * That is not a theoretical concern: a founder-reported intake bug was chased
 * as a gate defect before the screenshots showed a pre-fix bundle still running
 * on a phone days after the fix reached production (hub #160). Without this the
 * same failure mode swallows every future front-end fix.
 *
 * The check compares the build id compiled into this bundle against
 * `build-id.json`, which is published beside it and served `no-cache`.
 */

/** How often to re-check while the tab is open. */
const POLL_INTERVAL_MS = 10 * 60 * 1000;

export interface BuildVersionState {
  /** True once the deployed build id differs from the running one. */
  updateAvailable: boolean;
  /** The build this tab is running. */
  currentBuildId: string;
  /** The build currently deployed, once known. */
  deployedBuildId: string | null;
  /** Hard-reload onto the new build. */
  reload: () => void;
}

async function fetchDeployedBuildId(signal: AbortSignal): Promise<string | null> {
  // Cache-busted AND no-store: belt and braces, since a misconfigured
  // cache-control on build-id.json would otherwise pin this to the old value
  // and quietly disable the whole check.
  const response = await fetch(`/build-id.json?t=${Date.now()}`, {
    cache: 'no-store',
    signal,
  });
  if (!response.ok) return null;
  const body: unknown = await response.json();
  const buildId = (body as { buildId?: unknown } | null)?.buildId;
  return typeof buildId === 'string' && buildId ? buildId : null;
}

export function useBuildVersion(): BuildVersionState {
  const currentBuildId = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';
  const [deployedBuildId, setDeployedBuildId] = useState<string | null>(null);
  // Once stale, stay stale: a later failed poll must not retract the prompt.
  const settled = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const check = async () => {
      if (cancelled || settled.current || document.visibilityState === 'hidden') return;
      try {
        const deployed = await fetchDeployedBuildId(controller.signal);
        if (cancelled || !deployed) return;
        setDeployedBuildId(deployed);
        if (deployed !== currentBuildId) settled.current = true;
      } catch {
        // Offline, blocked, or no build-id.json (dev server). Staleness is not
        // worth surfacing an error over — try again on the next tick.
      }
    };

    void check();
    const timer = window.setInterval(check, POLL_INTERVAL_MS);
    // Returning to a tab left open for days is exactly when this matters.
    document.addEventListener('visibilitychange', check);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [currentBuildId]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    updateAvailable: deployedBuildId != null && deployedBuildId !== currentBuildId,
    currentBuildId,
    deployedBuildId,
    reload,
  };
}
